import React, { useEffect, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import axios from "axios";
import { GridColDef } from "@mui/x-data-grid";

import { useAuth } from "../../../context/AuthContext";
import { ReusableDataGrid } from "../../../components/shared/ReusableDataGrid";
import { CustomBox } from "../../Shared/CustomBox/CustomBox";
import { CustomImage } from "../../Shared/CustomImage/CustomImage";
import { CustomTypography } from "../../Shared/CustomTypography/CustomTypography";
import { CustomButton } from "../../Shared/CustomButton/CustomButton";
import { useNavigate } from "react-router-dom";

import { ReferralDetailsDialog } from "./ReferralDetailsDialog";
import { downloadReferralToPDF } from "../../utils/downloadReferralPDF";

import { Visibility, Download } from "@mui/icons-material";

// ---------------------
// MAPPER (Updated)
// ---------------------
const mapReferralData = (referrals: any[]) => {
  return referrals.map((ref) => ({
    id: ref.id,
    date: new Date(ref.created_at).toLocaleDateString(),
    reason: ref.reason,
    notes: ref.notes,
    status: ref.status,
    distance: "5.84 mi",
    travelTime: "19.0 min",
    workingHours: "11:00 AM to 04:00 PM",
    healthPlans: "Aetna, Anthem Blue Cross, Blue Shield of California",
    location: "Lakeview Medical Center, Seattle, WA",

    referredBy: {
      id: ref.referral_from_provider_id,
      email: ref.from_provider?.email || "-",
      avatar: "https://randomuser.me/api/portraits/men/32.jpg",
      name: ref.from_provider?.email?.split("@")[0] || "Unknown Doctor",
      specialty: "N/A",
      contact: "+1 206 555-1234",
      hospital: "Seattle Health Institute",
    },

    referredTo: {
      id: ref.referral_to_provider_id,
      email: ref.to_provider?.email || "-",
      avatar: "https://randomuser.me/api/portraits/men/32.jpg",
      name: ref.to_provider?.email?.split("@")[0] || "Unknown Doctor",
      specialty: "N/A",
      contact: "+1 123-456-7890",
      hospital: "Lakeview Medical Center",
    },

    patient: {
      name: "Jane Cooper",
      age: "33",
      gender: "Female",
      email: "john@example.com",
      contact: "+1 206 555-6789",
      mrn: "45231",
    },

    clinicalDetails: {
      presentingComplaint: "Lower abdominal pain, increased frequency of urination, burning micturition, occasional hematuria.",
      historyOfPresentIllness: "Symptoms started 2 weeks ago, gradually worsening. Pain radiates towards the groin. Reports nocturia (3–4 times/night). No history of trauma. Previous hospitalization for urinary tract infection 1 year ago.",
      pastMedicalHistory: "Hypertension (10 years), Type 2 Diabetes Mellitus (8 years).",
      currentMedications: "Aspirin 75 mg daily, Metoprolol 50 mg BID, Metformin 500 mg BID.",
      allergies: "No known drug allergies.",
      vitalSigns: "BP: 150/95 mmHg, HR: 92 bpm, Temp: 98.4°F, SpO₂: 95%.",
      investigations: "• Urinalysis: Presence of RBCs and WBCs\n• Ultrasound KUB: Mild left hydronephrosis, possible ureteric calculus (6 mm)\n• Serum Creatinine: Mildly elevated",
    },
  }));
};

// Download PDF function - Removed, now using utility
// const downloadReferralOrderPDF = ... (removed)

export const PatientReferral = () => {
  const [referrals, setReferrals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReferral, setSelectedReferral] = useState(null);

  const { user } = useAuth();
  const token = user?.token;
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const refRes = await axios.get(
          `https://vivotal-backend.vercel.app/api/v1/referrals/patient/${user?.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        // API: refRes.data.data contains array
        const mapped = mapReferralData(refRes?.data?.data);
        setReferrals(mapped);
      } catch (err) {
        console.error("Error loading referrals", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [token, user]);

  // -------------------------
  // Datagrid Columns
  // -------------------------
  const columns: GridColDef[] = [
    {
      field: "referredBy",
      headerName: "Referred By",
      width: 250,
      renderCell: (params) => (
        <CustomBox gap={1} mt={1}>
          <CustomImage
            src={params.value.avatar}
            width={40}
            height={40}
            radius="50%"
          />
          <CustomBox flexDirection="column">
            <CustomTypography fontWeight={600}>
              {params.value.name}
            </CustomTypography>
            <CustomTypography fontSize={12} color="gray">
              {params.value.email}
            </CustomTypography>
          </CustomBox>
        </CustomBox>
      ),
    },

    {
      field: "referredTo",
      headerName: "Referred To",
      width: 250,
      renderCell: (params) => (
        <CustomBox
          gap={1}
          mt={1}
          sx={{ cursor: "pointer" }}
          onClick={() =>
            navigate("/user-layout/patient-referral-to", {
              state: { referral: params.row },
            })
          }
        >
          <CustomImage
            src={params.value.avatar}
            width={40}
            height={40}
            radius="50%"
          />
          <CustomBox flexDirection="column">
            <CustomTypography fontWeight={600}>
              {params.value.name}
            </CustomTypography>
            <CustomTypography fontSize={12} color="gray">
              {params.value.email}
            </CustomTypography>
          </CustomBox>
        </CustomBox>
      ),
    },

    { field: "date", headerName: "Referral Date", width: 150 },
    { field: "reason", headerName: "Reason", width: 250 },

    {
      field: "ReferralOrder",
      headerName: "Referral Order",
      width: 200,
      renderCell: (params) => (
        <CustomBox>
          <CustomButton onClick={() =>
            navigate("/user-layout/referral-order", {
              state: { referral: params.row },
            })
          }>
            <Visibility sx={{ fontSize: 22, color: "#1976d2" }} />
          </CustomButton>

          <CustomButton onClick={() => downloadReferralToPDF(params.row, params.row.patient, params.row.clinicalDetails)}>
            <Download sx={{ fontSize: 22, color: "#1976d2" }} />
          </CustomButton>
        </CustomBox>
      ),
    },
  ];

  return (
    <Box width="100%">
      <Box display="flex" justifyContent="space-between" px={2} py={1}>
        <Typography variant="h6" fontWeight={600}>
          Referral List
        </Typography>
        <Typography>Total Count: {referrals.length}</Typography>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Box px={2}>
          <ReusableDataGrid columns={columns} data={referrals} />
        </Box>
      )}

      <ReferralDetailsDialog
        open={Boolean(selectedReferral)}
        onClose={() => setSelectedReferral(null)}
        referral={selectedReferral}
      />
    </Box>
  );
};
